import { useEffect, useState } from "react";
import { Plus, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Jawatankuasa() {
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [members, setMembers] = useState([]);
  const [openTerm, setOpenTerm] = useState(false);
  const [openMember, setOpenMember] = useState(false);
  const [termForm, setTermForm] = useState({ name: "", start_year: 2026, end_year: 2028 });
  const [memberForm, setMemberForm] = useState({ display_name: "", position: "", position_order: 10 });

  const loadTerms = async () => {
    const res = await api.get("/committee/terms");
    setTerms(res.data);
    if (res.data.length > 0 && !selectedTerm) setSelectedTerm(res.data.find((t) => t.is_active)?.id || res.data[0].id);
  };

  const loadMembers = async (termId) => {
    if (!termId) return;
    const res = await api.get("/committee/members", { params: { term_id: termId } });
    setMembers(res.data);
  };

  useEffect(() => { loadTerms(); }, []);
  useEffect(() => { loadMembers(selectedTerm); }, [selectedTerm]);

  const submitTerm = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/committee/terms", termForm);
      toast.success("Penggal ditambah");
      setOpenTerm(false);
      await loadTerms();
      setSelectedTerm(res.data.id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const activateTerm = async (id) => {
    await api.put(`/committee/terms/${id}/activate`);
    toast.success("Penggal diaktifkan");
    loadTerms();
  };

  const submitMember = async (e) => {
    e.preventDefault();
    try {
      await api.post("/committee/members", { ...memberForm, term_id: selectedTerm });
      toast.success("Ahli jawatankuasa ditambah");
      setOpenMember(false);
      setMemberForm({ display_name: "", position: "", position_order: 10 });
      loadMembers(selectedTerm);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-jawatankuasa-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-piks-black">Jawatankuasa</h1>
          <p className="text-sm text-piks-muted">Urus penggal dan ahli jawatankuasa PPPS.</p>
        </div>
        <Dialog open={openTerm} onOpenChange={setOpenTerm}>
          <DialogTrigger asChild><Button variant="outline" data-testid="add-term-btn"><Plus className="w-4 h-4 mr-1.5" /> Penggal Baharu</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Penggal Jawatankuasa Baharu</DialogTitle></DialogHeader>
            <form onSubmit={submitTerm} className="space-y-4">
              <div><Label>Nama Penggal</Label><Input required data-testid="term-name-input" value={termForm.name} onChange={(e) => setTermForm({ ...termForm, name: e.target.value })} className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Tahun Mula</Label><Input type="number" required value={termForm.start_year} onChange={(e) => setTermForm({ ...termForm, start_year: parseInt(e.target.value) })} className="mt-1.5" /></div>
                <div><Label>Tahun Tamat</Label><Input type="number" required value={termForm.end_year} onChange={(e) => setTermForm({ ...termForm, end_year: parseInt(e.target.value) })} className="mt-1.5" /></div>
              </div>
              <Button type="submit" className="w-full bg-piks-black hover:bg-piks-black/90">Simpan</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {terms.map((t) => (
          <button key={t.id} onClick={() => setSelectedTerm(t.id)} data-testid={`term-select-${t.id}`} className={`px-4 py-2 rounded-full text-sm font-medium border ${selectedTerm === t.id ? "bg-piks-black text-white border-piks-black" : "bg-white text-piks-black border-piks-border"}`}>
            {t.name} {t.is_active && <CheckCircle2 className="w-3.5 h-3.5 inline ml-1 text-piks-gold" />}
          </button>
        ))}
      </div>

      {selectedTerm && !terms.find((t) => t.id === selectedTerm)?.is_active && (
        <Button size="sm" variant="outline" onClick={() => activateTerm(selectedTerm)} data-testid="activate-term-btn" className="mb-6">Jadikan Penggal Aktif</Button>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide">Ahli Jawatankuasa</h2>
        <Dialog open={openMember} onOpenChange={setOpenMember}>
          <DialogTrigger asChild><Button size="sm" data-testid="add-committee-member-btn" className="bg-piks-black hover:bg-piks-black/90"><Plus className="w-4 h-4 mr-1.5" /> Tambah Ahli</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Tambah Ahli Jawatankuasa</DialogTitle></DialogHeader>
            <form onSubmit={submitMember} className="space-y-4">
              <div><Label>Nama</Label><Input required data-testid="committee-member-name-input" value={memberForm.display_name} onChange={(e) => setMemberForm({ ...memberForm, display_name: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Jawatan</Label><Input required data-testid="committee-member-position-input" placeholder="Contoh: Presiden" value={memberForm.position} onChange={(e) => setMemberForm({ ...memberForm, position: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Susunan Paparan</Label><Input type="number" value={memberForm.position_order} onChange={(e) => setMemberForm({ ...memberForm, position_order: parseInt(e.target.value) })} className="mt-1.5" /></div>
              <Button type="submit" className="w-full bg-piks-black hover:bg-piks-black/90">Simpan</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((m) => (
          <div key={m.id} className="p-4 rounded-lg bg-white border border-piks-border" data-testid={`admin-committee-member-${m.id}`}>
            <p className="font-medium text-piks-black">{m.display_name}</p>
            <p className="text-xs text-piks-red font-medium">{m.position}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
